import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Stack,
  Alert
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import ExamQuestionPayloadEditor from './ExamQuestionPayloadEditor';

const QT = [
  { value: 'true_false', label: 'True / False' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'single_choice', label: 'Single choice' },
  { value: 'multiple_choice', label: 'Multiple choice' },
  { value: 'short_text', label: 'Short text' },
  { value: 'match', label: 'Match' }
];

const defaultPayload = (type) => {
  switch (type) {
    case 'true_false':
      return { correct: true };
    case 'yes_no':
      return { correct: 'yes' };
    case 'single_choice':
      return { options: [{ key: 'a', text: 'A' }, { key: 'b', text: 'B' }], correct: 'a' };
    case 'multiple_choice':
      return { options: [{ key: 'a', text: 'A' }], correct: ['a'] };
    case 'short_text':
      return { expected: '', case_insensitive: true };
    case 'match':
      return { left: ['A', 'B'], right: ['1', '2'], pairs: [[0, 0], [1, 1]] };
    default:
      return {};
  }
};

const TrainingQuestionBank = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('training_add') || hasPermission('training_modify');
  const [categories, setCategories] = useState([]);
  const [spots, setSpots] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [spotId, setSpotId] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    question_type: 'true_false',
    prompt: '',
    points: 1,
    learning_spot_id: '',
    payload: defaultPayload('true_false')
  });

  const loadCats = useCallback(async () => {
    const r = await api.get('/training/categories');
    setCategories(r.data.data || []);
  }, []);

  const loadSpots = useCallback(async () => {
    if (!categoryId) {
      setSpots([]);
      return;
    }
    const r = await api.get('/training/learning-spots', { params: { category_id: categoryId } });
    setSpots(r.data.data || []);
  }, [categoryId]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (categoryId) params.category_id = categoryId;
      if (spotId) params.learning_spot_id = spotId;
      const r = await api.get('/training/question-bank', { params });
      setRows(r.data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [categoryId, spotId]);

  useEffect(() => {
    loadCats();
  }, [loadCats]);
  useEffect(() => {
    loadSpots();
  }, [loadSpots]);
  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const openNew = () => {
    if (!categoryId) {
      toast.error('Select a category first');
      return;
    }
    setEditing(null);
    setForm({
      question_type: 'true_false',
      prompt: '',
      points: 1,
      learning_spot_id: spotId || '',
      payload: defaultPayload('true_false')
    });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row.id);
    setForm({
      question_type: row.question_type,
      prompt: row.prompt,
      points: row.points,
      learning_spot_id: row.learning_spot_id || '',
      payload: row.payload && typeof row.payload === 'object' ? row.payload : defaultPayload(row.question_type)
    });
    setOpen(true);
  };

  const save = async () => {
    if (!categoryId || !form.prompt?.trim()) {
      toast.error('Category and prompt required');
      return;
    }
    try {
      const body = {
        category_id: categoryId,
        learning_spot_id: form.learning_spot_id || null,
        question_type: form.question_type,
        prompt: form.prompt.trim(),
        points: parseFloat(form.points) || 1,
        payload: form.payload
      };
      if (editing) {
        await api.put(`/training/question-bank/${editing}`, body);
        toast.success('Updated');
      } else {
        await api.post('/training/question-bank', body);
        toast.success('Created');
      }
      setOpen(false);
      loadRows();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this bank item?')) return;
    try {
      await api.delete(`/training/question-bank/${id}`);
      toast.success('Deleted');
      loadRows();
    } catch (e) {
      toast.error('Failed');
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/training/catalog')} sx={{ mb: 2 }}>
        Catalog
      </Button>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        Question bank
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Store reusable questions by category and optional sub-category. Copy prompts into exams from the Exams tab, or add questions here first.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel>Category</InputLabel>
          <Select label="Category" value={categoryId} onChange={(e) => { setCategoryId(e.target.value); setSpotId(''); }}>
            <MenuItem value="">—</MenuItem>
            {categories.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 220 }} disabled={!categoryId}>
          <InputLabel>Sub-category</InputLabel>
          <Select label="Sub-category" value={spotId} onChange={(e) => setSpotId(e.target.value)}>
            <MenuItem value="">Any / none</MenuItem>
            {spots.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {canEdit && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew} disabled={!categoryId}>
            Add to bank
          </Button>
        )}
      </Stack>

      {!categoryId && <Alert severity="info">Choose a category to list and manage bank items.</Alert>}

      {categoryId && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Prompt</TableCell>
                <TableCell>Sub-category</TableCell>
                <TableCell align="right">Pts</TableCell>
                {canEdit && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5}>Loading…</TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.question_type}</TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>{r.prompt}</TableCell>
                    <TableCell>{r.learningSpot?.name || '—'}</TableCell>
                    <TableCell align="right">{r.points}</TableCell>
                    {canEdit && (
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEdit(r)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => del(r.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>No items yet.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit bank question' : 'New bank question'}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={form.question_type}
              onChange={(e) => {
                const t = e.target.value;
                setForm({ ...form, question_type: t, payload: defaultPayload(t) });
              }}
            >
              {QT.map((x) => (
                <MenuItem key={x.value} value={x.value}>
                  {x.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal" disabled={!categoryId}>
            <InputLabel>Sub-category (optional)</InputLabel>
            <Select
              label="Sub-category (optional)"
              value={form.learning_spot_id}
              onChange={(e) => setForm({ ...form, learning_spot_id: e.target.value })}
            >
              <MenuItem value="">—</MenuItem>
              {spots.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            label="Prompt"
            multiline
            minRows={2}
            value={form.prompt}
            onChange={(e) => setForm({ ...form, prompt: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            type="number"
            label="Points"
            value={form.points}
            onChange={(e) => setForm({ ...form, points: e.target.value })}
          />
          <ExamQuestionPayloadEditor
            questionType={form.question_type}
            payload={form.payload}
            onChange={(p) => setForm({ ...form, payload: p })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingQuestionBank;
